import type {
  Affiliate,
  DiscountCode,
  ExternalLink,
  ExternalStockStatus,
  Image,
  InternalLink,
  Price,
  Shipping
} from '../capi/shared';

export type Content =
  | Header
  | Paragraphs
  | List
  | Quote
  | DynamicProductBlock
  | RegularProductBlock
  | LegacyHtml
  | Video
  | Embed
  | Image;

export type Header = {
  level: HeaderLevel;
  text: string;
  type: 'header';
};

export enum HeaderLevel {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
  h6 = 'h6'
}

export type Paragraphs = {
  items: ParagraphItem[];
  type: 'paragraphs';
};

export type ParagraphItem = {
  content: ParagraphItemContent;
};

export type ParagraphItemContent = {
  items: Text[];
};

export type Text = PlainText | InternalLink | ExternalLink;

export type PlainText = {
  style?: PlainTextStyle;
  text: string;
  type: 'text';
};

export enum PlainTextStyle {
  BOLD = 'bold',
  ITALICS = 'italics'
}

export type List = {
  items: ListItem[];
  list_type: 'ordered' | 'unordered';
  type: 'list';
};

export type ListItem = {
  content: {
    items: (Text | InternalLink | ExternalLink)[];
  };
};

export type DynamicProductBlock = {
  affiliate: Affiliate;
  cta: ExternalCta | InternalCta | ProductReminderCta;
  header: string;
  image: Image;
  other_affiliates_link: InternalLink;
  price: Price;
  product_id: number;
  product_name: string;
  release_date: number;
  shipping: Shipping;
  sizes: string[];
  stock_status: ExternalStockStatus;
  type: 'dynamic_product_block';
};

type InternalCta = {
  path: string;
  text: string;
  type: 'internal';
};

type ProductReminderCta = {
  legacy_id: string;
  text: string;
  type: 'product_reminder';
};

export type ExternalCta = {
  click_ref_template: string;
  text: string;
  type: 'external_product_link';
  url: string;
  url_template: string;
};

export type RegularProductBlock = {
  cta: RegularProductBlockCta;
  header: string;
  image?: Image;
  type: 'product_block';
};

type RegularProductBlockCta = {
  affiliate?: Affiliate;
  button_text: string;
  click_ref_template: string;
  cta_type: string[];
  discount_code?: DiscountCode;
  price: Price;
  shipping: Shipping;
  url: string;
  url_template: string;
};

export type Embed = {
  html?: string;
  id?: string;
  mobile_hidden?: boolean;
  site: 'instagram' | 'twitter' | 'facebook' | 'tiktok';
  type: 'embed';
  url?: string;
};

export type Quote = {
  speech_marks: boolean;
  text: string;
  type: 'quote';
};

export type LegacyHtmlText = {
  html: string;
  type: 'legacy_html';
};

export type Video = {
  autoplay?: boolean;
  id: string;
  loop?: boolean;
  site: string;
  type: 'video';
  url?: string;
};

type LegacyHtml = LegacyHtmlText | InternalLink | ExternalLink;
